export type HarnessName = 'Codex' | 'Claude Code' | 'Cursor' | 'OpenCode';

export interface SessionRecord {
  id: string;
  harness: HarnessName;
  title: string;
  start: number;
  duration: number;
  state: 'complete' | 'running' | 'stalled';
  turns: number;
  verification: number;
}

export const sessions: SessionRecord[] = [
  { id: 's1', harness: 'Codex', title: 'Trace release regression', start: 0.2, duration: 1.1, state: 'complete', turns: 18, verification: 92 },
  { id: 's2', harness: 'Claude Code', title: 'Reconcile interface contract', start: 1.0, duration: 1.7, state: 'complete', turns: 31, verification: 84 },
  { id: 's3', harness: 'Cursor', title: 'Prototype event timeline', start: 2.35, duration: 2.0, state: 'complete', turns: 26, verification: 78 },
  { id: 's4', harness: 'OpenCode', title: 'Audit provider boundary', start: 3.25, duration: 1.55, state: 'stalled', turns: 24, verification: 61 },
  { id: 's5', harness: 'Codex', title: 'Stabilize deployment path', start: 5.0, duration: 2.4, state: 'complete', turns: 42, verification: 73 },
  { id: 's6', harness: 'Claude Code', title: 'Review generated migration', start: 6.15, duration: 1.8, state: 'complete', turns: 29, verification: 57 },
  { id: 's7', harness: 'Cursor', title: 'Repair interaction states', start: 8.2, duration: 2.1, state: 'running', turns: 38, verification: 44 },
  { id: 's8', harness: 'Codex', title: 'Retry release candidate', start: 9.7, duration: 2.8, state: 'running', turns: 51, verification: 36 },
  { id: 's9', harness: 'OpenCode', title: 'Cross-check rollback', start: 11.4, duration: 1.7, state: 'stalled', turns: 33, verification: 28 },
];

export const harnesses: Array<{ name: HarnessName; model: string; status: string; load: number }> = [
  { name: 'Codex', model: 'gpt-5.4', status: '2 active', load: 82 },
  { name: 'Claude Code', model: 'sonnet-5', status: 'reviewing', load: 61 },
  { name: 'Cursor', model: 'composer-2', status: 'editing', load: 54 },
  { name: 'OpenCode', model: 'gemini-3.1', status: 'stalled', load: 37 },
];

export const patterns = [
  {
    id: 'recovery-deferral',
    title: 'Recovery deferral',
    strength: 88,
    direction: 'rising',
    summary: 'Low-cost opportunities to step away are repeatedly passed while new agent work begins.',
    support: ['132 minutes of continuous interaction', 'Three pause opportunities passed', 'Planned stopping point moved twice'],
    counter: 'One long interval included passive model wait time.',
  },
  {
    id: 'cognitive-tunneling',
    title: 'Cognitive tunneling',
    strength: 74,
    direction: 'stable',
    summary: 'Attention narrows around resolving agent output while basic body check-ins disappear from the flow.',
    support: ['Four overlapping lanes', 'Corrective loops increased', 'No self-check recorded after hour ten'],
    counter: 'Parallel work was deliberate and useful earlier in the day.',
  },
  {
    id: 'boundary-erosion',
    title: 'Boundary erosion',
    strength: 67,
    direction: 'rising',
    summary: 'A chosen stopping point is repeatedly extended by one more agent task.',
    support: ['Two stop-time extensions', 'Late-stage task switching', 'No end-of-day transition'],
    counter: 'One extension prevented a real release defect.',
  },
];

export const interventionHistory = [
  { when: '2 days ago', action: '7-minute off-screen reset', response: 'Helpful', effect: 'Returned with lower strain' },
  { when: '5 days ago', action: 'Close-the-day protocol', response: 'Helpful', effect: 'Stopped 38 min earlier' },
  { when: '8 days ago', action: 'Movement + water check', response: 'Mistimed', effect: 'Suppressed during deploys' },
  { when: '12 days ago', action: 'Eyes + posture reset', response: 'Neutral', effect: 'Shortened to 3 minutes' },
];

export function formatClock(hourFromStart: number): string {
  const totalMinutes = Math.round((8.4 + hourFromStart) * 60);
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${suffix}`;
}
